<template>
  <div class="dashboard-container">
    <b-container>
      <h2 class="dashboard-title">Dashboard</h2>

      <!-- Stats Cards -->
      <b-row>
        <b-col md="3" v-for="(stat, key) in stats" :key="key">
          <b-card class="status-stat-card">
            <div class="status-card-icon">
              <b-icon :icon="stat.icon" :variant="stat.variant" />
            </div>
            <h3 class="status-card-value">{{ stat.value }}</h3>
            <p class="status-card-title">{{ stat.title }}</p>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="content-section">
        <!-- Recent Jobs Table -->
        <b-col md="8">
          <b-table
            striped
            hover
            :items="recentJobs"
            :fields="jobFields"
            class="custom-table"
            small
            responsive
          >
            <template #cell(name)="data">
              <b-link
                :to="'/automation-detail/' + data.item._id"
                class="job-link"
              >
                {{ data.item.name }}
              </b-link>
            </template>

            <template #cell(status)="data">
              <span
                :class="{
                  'status-pill success': data.item.status === 'success',
                  'status-pill failed': data.item.status === 'failed',
                }"
              >
                {{ data.item.status }}
              </span>
            </template>
          </b-table>
        </b-col>
        <!-- Chart -->
        <b-col md="4" class="chart-container">
          <canvas ref="chart"></canvas>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import axios from "axios";

export default {
  data() {
    return {
      stats: {},
      recentJobs: [],
      jobFields: [
        { key: "name", label: "Job Name" },
        { key: "status", label: "Status" },
      ],
    };
  },
  async mounted() {
    const res = await axios.get("/api/dashboard-data");
    this.stats = {
      totalJobs: {
        title: "Total Jobs",
        value: res.data.jobs.total || 0,
        icon: "briefcase",
        variant: "primary",
      },
      successfulPosts: {
        title: "Successful Posts",
        value: res.data.posts.successful || 0,
        icon: "check-circle",
        variant: "success",
      },
      failedPosts: {
        title: "Failed Posts",
        value: res.data.posts.failed || 0,
        icon: "x-circle",
        variant: "danger",
      },
      upcomingPosts: {
        title: "Upcoming Posts",
        value: res.data.upcomingPosts || 0,
        icon: "clock",
        variant: "warning",
      },
    };
    this.recentJobs = res.data.recentJobs || [];
    this.renderChart(
      res.data.posts.successful || 0,
      res.data.posts.failed || 0
    );
  },
  methods: {
    renderChart(success, failed) {
      if (this.chart) this.chart.destroy(); // Ensure chart re-renders correctly
      this.chart = new Chart(this.$refs.chart, {
        type: "doughnut",
        data: {
          labels: ["Success", "Failed"],
          datasets: [
            {
              label: "Post Performance",
              data: [success, failed],
              backgroundColor: ["#28a745", "#dc3545"],
              borderColor: "#fff",
              borderWidth: 2,
              hoverOffset: 10,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                color: "#333",
                font: {
                  size: 14,
                  weight: "bold",
                },
              },
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  let label = context.label || "";
                  let value = context.raw || 0;
                  let total = context.chart._metasets[0].total;
                  let percentage = ((value / total) * 100).toFixed(1);
                  return `${label}: ${value} (${percentage}%)`;
                },
              },
            },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  padding: 30px;
  background: #eef1f8;
  border-radius: 10px;
}
.dashboard-title {
  font-size: 28px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 30px;
}
.status-stat-card {
  text-align: center;
  background-color: #ffffff;
  padding: 1.75rem;
  border-radius: 16px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.3s ease;
  border: none;
  cursor: pointer;
  height: 200px !important;
}

.status-stat-card:hover {
  transform: translateY(-4px);
  background: #e5e7eb;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
}

.status-card-icon {
  font-size: 32px;
  color: #adb5bd;
  margin-bottom: 10px;
  transition: color 0.3s ease;
}

.status-stat-card:hover .status-card-icon {
  color: #e5e7eb !important;
}

.status-card-value {
  font-size: 2rem;
  font-weight: 600;
  color: #212529;
  margin-bottom: 4px;
}

.status-card-title {
  font-size: 0.95rem;
  color: #6c757d;
  margin: 0;
}
.custom-table {
  background: white;
  border-radius: 12px;
  box-shadow: 0px 5px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.chart-container {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0px 5px 8px rgba(0, 0, 0, 0.1);
}
.content-section {
  margin-top: 35px !important;
}

.custom-table {
  border: 1px solid #050505;
  border-collapse: separate;
  border-spacing: 0;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  border-radius: 12px;
  overflow: hidden;
  background-color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
}

.custom-table thead {
  background: red;
  color: #6c757d;
  font-weight: 600;
}

.custom-table thead th {
  background-color: #050505;
  color: #f7f7f7;
  font-weight: 600;
  border-bottom: 1px solid #e5e7eb;
}

.custom-table tbody td {
  padding-left: 10px !important;
}

.custom-table tbody tr {
  text-align: left;
  background-color: #fff;
  border-bottom: 1px solid #e5e7eb;
  transition: background-color 0.2s ease-in-out;
}

.custom-table tbody tr:hover {
  background-color: #f1f5f9;
  transition: background-color 0.2s ease-in-out;
}

.job-link {
  color: #0d6efd;
  font-weight: 500;
  text-decoration: none;
}

.job-link:hover {
  text-decoration: underline;
}

.status-pill {
  display: inline-block;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 9999px;
  text-transform: capitalize;
}

.status-pill.success {
  background-color: #d1fae5;
  color: #065f46;
}

.status-pill.failed {
  background-color: #fee2e2;
  color: #991b1b;
}
</style>
